import { useServices, useToast } from '@/app/providers';
import { Button, Card, Input } from '@/core/ui/components';
import { AlertCircle, Loader2, Save, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

interface HikCentralVisitorGroupConfigProps {
  tenantId: string;
}

export function HikCentralVisitorGroupConfig({ tenantId }: HikCentralVisitorGroupConfigProps) {
  const { integrationCatalogRepository } = useServices();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [integrationId, setIntegrationId] = useState<string | null>(null);
  const [initialValue, setInitialValue] = useState<string>('');
  const [value, setValue] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const cfg = await integrationCatalogRepository.getProviderConfig('HIKCENTRAL', tenantId);
        if (cancelled) return;
        if (!cfg.integrationId) {
          setError('No hay una integración HikCentral configurada para este tenant.');
          return;
        }
        const current = (cfg.config?.visitorGroupName as string | undefined) ?? '';
        setIntegrationId(cfg.integrationId);
        setInitialValue(current);
        setValue(current);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : 'Error al cargar configuración HikCentral';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [integrationCatalogRepository, tenantId]);

  const dirty = value.trim() !== initialValue.trim();

  const handleSave = async () => {
    if (!integrationId) return;
    setSaving(true);
    try {
      const trimmed = value.trim();
      await integrationCatalogRepository.updateConfig(
        integrationId,
        { visitorGroupName: trimmed },
        tenantId,
      );
      setInitialValue(trimmed);
      setValue(trimmed);
      showToast('success', 'Nombre del Visitor Group actualizado.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error al guardar configuración';
      showToast('error', msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-indigo-500" />
        <h3 className="text-base font-semibold text-theme">Visitor Group (HikCentral)</h3>
      </div>

      {loading && (
        <div className="flex justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
        </div>
      )}

      {!loading && error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && integrationId && (
        <>
          <Input
            label="Nombre del Visitor Group"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Visitors"
            disabled={saving}
          />
          <p className="text-xs text-theme-secondary mt-2">
            Debe coincidir exactamente con el nombre del grupo en HikCentral (Visitor Management → Visitor Group).
            Si se deja vacío se usará el default <code className="font-mono">"Visitors"</code>.
          </p>
          <div className="flex justify-end mt-3">
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Save className="w-4 h-4" />}
              disabled={!dirty || saving}
              isLoading={saving}
              onClick={handleSave}
            >
              Guardar
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
